import { CoreEvent, eventNames } from '@carvana/core-events';
import { SearchSource } from '@carvana/merch-contracts';
import { DispatcherEvent, NavPayloadAction } from '@carvana/merch-native';
import type {
  ErrorContext,
  EventData,
  SearchBarContextProps,
  SuggestionSelectionProps
} from '@carvana/merch-search-bar';
import { useCallback, useContext, useMemo } from 'react';
import { OPEN_TEXT_SEARCH_EXPERIMENT } from '~/constants/common/openTextSearch';
import { isValidSortOption } from '~/constants/common/SortOption';
import { apikUrl, assetsCdnUrl } from '~/constants/environment';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import AppliedFiltersContext from '~/context/common/AppliedFiltersContext';
import {
  useIsMobile,
  useIsNative,
  useIsTablet
} from '~/context/common/DeviceContext';
import { useSavedSearches } from '~/context/common/SavedSearchesContext';
import { useConfiguration } from '~/context/common/StaticContext';
import { useUserLocation } from '~/context/common/UserContext';
import { useDealershipId } from '~/context/dealership/StaticContext';
import InventoryContext from '~/context/srp/InventoryContext';
import ModalContext from '~/context/srp/ModalContext';
import SearchEventsDataContext from '~/context/srp/SearchEventsDataContext';
import { StickySearchContext } from '~/context/srp/StickySearchProvider';
import useIsInPriceDropSuppressionExperiment from '~/hooks/common/experiments/useIsInPriceDropSuppressionExperiment';
import useIsInTextSearchExperiment from '~/hooks/common/experiments/useIsInTextSearchExperiment';
import useGetOpenTextSearch from '~/hooks/srp/useGetOpenTextSearch';
import useTrackRendered from '~/hooks/srp/useTrackRendered';
import sendMessageToNative from '~/native/srp/dispatchers/sendMessageToNative';
import decodeCvnaId from '~/utilities/cvnaId';
import { generateErrorLogger } from '~/utilities/loggers';

export default function useSearchBarProps(
  inputValue: string
): SearchBarContextProps {
  const { setAppliedFilters, setSortBy } = useContext(AppliedFiltersContext);
  const { setFreeTextNoResults } = useContext(InventoryContext);
  const {
    isSearchBarFocused,
    isSearchBarModalOpen,
    setIsSearchBarFocused,
    setSearchBarModalOpen
  } = useContext(ModalContext);
  const { setSearchSource, setKeywordSuggestion } = useContext(
    SearchEventsDataContext
  );
  const { isStickySearchBar } = useContext(StickySearchContext);
  const { priceDropRestrictedStates, shouldUseTitleCasing } =
    useConfiguration();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isInTextSearchExperiment = useIsInTextSearchExperiment();
  const suppressPriceDropContent = useIsInPriceDropSuppressionExperiment();
  const savedSearches = useSavedSearches();
  const trackEvent = useTrackEvent();
  const { state = '' } = useUserLocation();
  const isNative = useIsNative();
  const dealershipId = useDealershipId();
  const getOpenTextSearch = useGetOpenTextSearch();
  // Dealerships are excluded from the open text search experiment
  const isTextSearchEnabled = dealershipId ? false : isInTextSearchExperiment;

  useTrackRendered(
    OPEN_TEXT_SEARCH_EXPERIMENT,
    (isSearchBarFocused || isSearchBarModalOpen) && inputValue.length > 0
  );

  const onCloseModal = useCallback(() => {
    setSearchBarModalOpen(false);
  }, [setSearchBarModalOpen]);

  const onSavedSearchViewAll = useCallback(() => {
    if (isNative) {
      sendMessageToNative(DispatcherEvent.Link, {
        action: NavPayloadAction.ViewSavedSearches
      });
    } else {
      CoreEvent.emit(eventNames.favorites.mfe.drawer.open, {
        tab: 'searches'
      });
    }
  }, [isNative]);

  const onShowModalClick = useCallback(() => {
    setSearchBarModalOpen(true);
  }, [setSearchBarModalOpen]);

  const onSuggestionSelection = useCallback(
    ({ cvnaId, eventData }: SuggestionSelectionProps) => {
      const searchSource = SearchSource[eventData.suggestionSource ?? ''];
      if (searchSource) {
        setSearchSource(searchSource);
      }
      setKeywordSuggestion({
        ...eventData,
        isStickySearchbar: isStickySearchBar
      });
      const decoded = decodeCvnaId(cvnaId);
      setFreeTextNoResults(false);
      setAppliedFilters(decoded?.filters ?? {});
      if (isValidSortOption(decoded?.sortBy)) {
        setSortBy(decoded?.sortBy);
      }
    },
    [
      isStickySearchBar,
      setAppliedFilters,
      setFreeTextNoResults,
      setKeywordSuggestion,
      setSearchSource,
      setSortBy
    ]
  );

  const onTextSearchSubmit = useCallback(
    ({ query, eventData }: { query: string; eventData: EventData }) => {
      setSearchSource(SearchSource.OpenTextSearch);
      setKeywordSuggestion({
        ...eventData,
        isStickySearchbar: isStickySearchBar
      });
      getOpenTextSearch(query);
    },
    [
      getOpenTextSearch,
      isStickySearchBar,
      setKeywordSuggestion,
      setSearchSource
    ]
  );

  const onError = useCallback((error: Error, context: ErrorContext) => {
    generateErrorLogger(`SearchBar - ${context.location}`, context)(error);
  }, []);

  return useMemo(
    () => ({
      apiBaseUrl: apikUrl,
      cdnBaseUrl: assetsCdnUrl,
      geolocationState: state,
      dealershipId: dealershipId ?? undefined,
      isMobile,
      isTablet,
      isTextSearchEnabled,
      onCloseModal,
      onError,
      onFocus: setIsSearchBarFocused,
      onSavedSearchViewAll,
      onShowModalClick,
      onSuggestionSelection,
      onTextSearchSubmit,
      priceDropRestrictedStates,
      refSource: 'srp',
      savedSearches,
      shouldUseTitleCasing,
      showButton: false,
      suppressPriceDropContent,
      trackEvent
    }),
    [
      dealershipId,
      isMobile,
      isTablet,
      isTextSearchEnabled,
      onCloseModal,
      onError,
      onSavedSearchViewAll,
      onShowModalClick,
      onSuggestionSelection,
      onTextSearchSubmit,
      priceDropRestrictedStates,
      savedSearches,
      setIsSearchBarFocused,
      shouldUseTitleCasing,
      state,
      suppressPriceDropContent,
      trackEvent
    ]
  );
}
